import React, { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAxios } from '../../utils/hook/useAxios';

const MONTHS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function generateHourOptions() {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      opts.push(
        <option key={t} value={t}>
          {t}
        </option>
      );
    }
  }
  return opts;
}

function generateDatesBetween(d1, d2) {
  const dates = [];
  const cur = new Date(d1);
  while (cur <= d2) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/* ══════════════ CalendarDayButton ══════════════ */
function CalendarDayButton({
  date,
  price,
  currentMonth,
  WeekDAY,
  VehiculeId,
  unavailable,
  onUnavailabilityAdded,
}) {
  const axiosInstance = useAxios();

  const year = new Date().getFullYear();
  const monthStr = String(parseInt(currentMonth) + 1).padStart(2, '0');
  const dateStr = String(date).padStart(2, '0');
  const dateFixed = `${year}-${monthStr}-${dateStr}`;

  const [open, setOpen] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [showUnavailForm, setShowUnavailForm] = useState(false);
  const [priceValue, setPriceValue] = useState('');
  const [currentDate, setCurrentDate] = useState(dateFixed);
  const [unavailStartDate, setUnavailStartDate] = useState(dateFixed);
  const [unavailEndDate, setUnavailEndDate] = useState(dateFixed);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('20:00');
  const [repeat, setRepeat] = useState(true);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [hasUnavailabilities, setHasUnavailabilities] = useState(false);

  const today = new Date();
  const isToday =
    date === today.getDate() &&
    parseInt(currentMonth) === today.getMonth() &&
    year === today.getFullYear();

  function loadUnavailabilities() {
    axiosInstance
      .get(`/unavailabilities/getbyvehiculeid-date/${VehiculeId}?date=${dateFixed}`)
      .then(({ data }) => {
        setUnavailabilities(data);
        setHasUnavailabilities(data.length > 0);
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (VehiculeId) loadUnavailabilities();
  }, [date]);

  function handleOpen() {
    loadUnavailabilities();
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    setShowPriceForm(false);
    setShowUnavailForm(false);
  }

  function handleEnregistrer() {
    const dates = generateDatesBetween(new Date(dateFixed), new Date(currentDate));
    dates.forEach(async (d) => {
      try {
        await axiosInstance.post('/pricings/', {
          vehiculeId: VehiculeId,
          date: d,
          price: priceValue,
        });
        toast.success('Prix personnalisé enregistré');
      } catch (e) {
        console.error(e);
      }
    });
    setShowPriceForm(false);
  }

  function handleDefinirIndisponibilite() {
    axiosInstance
      .post('/unavailabilities/', {
        vehiculeId: VehiculeId,
        start_date: unavailStartDate,
        end_date: unavailEndDate,
        start_time: startTime,
        end_time: endTime,
        type: repeat ? '0' : '1',
        WeekDAY,
      })
      .then(() => {
        toast.success('Indisponibilité ajoutée');
        loadUnavailabilities();
        setShowUnavailForm(false);
        if (onUnavailabilityAdded) onUnavailabilityAdded();
      })
      .catch(() => {});
  }

  const btnStyle = {
    aspectRatio: 'unset',
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    gap: '2px',
    padding: '4px 2px',
    background: unavailable ? '#e9ecef' : '#e8f4fd',
    transition: 'transform 0.1s, box-shadow 0.1s',
    outline: isToday ? '2px solid #ff6600' : 'none',
  };

  return (
    <div style={{ aspectRatio: '1', minWidth: 0 }}>
      {/* ── Bouton jour ── */}
      <button
        style={btnStyle}
        type="button"
        onClick={handleOpen}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: isToday ? 700 : 500,
            color: unavailable ? '#adb5bd' : '#212529',
          }}
        >
          {date}
        </span>
        {price && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: unavailable ? '#adb5bd' : '#ff6600',
              lineHeight: 1,
            }}
          >
            {price}
          </span>
        )}
        {unavailable && (
          <span style={{ fontSize: 8, color: '#adb5bd' }}>
            <i className="bi bi-slash-circle"></i>
          </span>
        )}
      </button>

      {/* ── Offcanvas détails du jour (react-bootstrap) ── */}
      <Offcanvas
        show={open}
        onHide={handleClose}
        placement="end"
        style={{ maxWidth: 420, width: '92vw' }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <div>
            <Offcanvas.Title className="fw-bold" style={{ fontSize: 16 }}>
              {date} {MONTHS[currentMonth]}
            </Offcanvas.Title>
            {unavailable ? (
              <small className="text-danger">
                <i className="bi bi-slash-circle me-1"></i>Indisponible
              </small>
            ) : (
              <small className="text-success">
                <i className="bi bi-check-circle me-1"></i>Disponible
              </small>
            )}
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {/* ── Section Prix ── */}
          <div className="p-3 border-bottom">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
                  Prix du jour
                </p>
                <p className="mb-0 text-primary fw-bold">{price || 'Non défini'}</p>
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowPriceForm((f) => !f)}
              >
                <i className={`bi ${showPriceForm ? 'bi-x' : 'bi-pencil'} me-1`}></i>
                {showPriceForm ? 'Annuler' : 'Modifier'}
              </button>
            </div>

            {showPriceForm && (
              <div className="bg-body-tertiary rounded-3 p-3 mt-2">
                <p className="small text-muted mb-3">
                  Définissez un prix personnalisé pour un ou plusieurs jours.
                </p>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Nouveau prix</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="ex: 45"
                      value={priceValue}
                      onChange={(e) => setPriceValue(e.target.value)}
                    />
                    <span className="input-group-text bg-primary text-white">€</span>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Jusqu'au</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={currentDate}
                    onChange={(e) => setCurrentDate(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary btn-sm w-100" onClick={handleEnregistrer}>
                  <i className="bi bi-check2 me-1"></i>Enregistrer
                </button>
              </div>
            )}
          </div>

          {/* ── Section Indisponibilités ── */}
          <div className="p-3 border-bottom">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
                Indisponibilités
              </p>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setShowUnavailForm((f) => !f)}
              >
                <i className={`bi ${showUnavailForm ? 'bi-x' : 'bi-plus'} me-1`}></i>
                {showUnavailForm ? 'Annuler' : 'Ajouter'}
              </button>
            </div>

            {showUnavailForm && (
              <div className="bg-body-tertiary rounded-3 p-3 mt-2">
                <p className="small text-muted mb-3">
                  Bloquez les périodes où votre voiture n'est pas disponible.
                </p>

                <div className="row g-2 mb-2">
                  <div className="col-7">
                    <label className="form-label small fw-semibold mb-1">Début</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={unavailStartDate}
                      onChange={(e) => setUnavailStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-5">
                    <label className="form-label small fw-semibold mb-1">Heure</label>
                    <select
                      className="form-select form-select-sm"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    >
                      {generateHourOptions()}
                    </select>
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-7">
                    <label className="form-label small fw-semibold mb-1">Fin</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={unavailEndDate}
                      onChange={(e) => setUnavailEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-5">
                    <label className="form-label small fw-semibold mb-1">Heure</label>
                    <select
                      className="form-select form-select-sm"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    >
                      {generateHourOptions()}
                    </select>
                  </div>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`repeat-${date}-${currentMonth}`}
                    checked={repeat}
                    onChange={(e) => setRepeat(e.target.checked)}
                  />
                  <label
                    className="form-check-label small"
                    htmlFor={`repeat-${date}-${currentMonth}`}
                  >
                    Répéter chaque semaine
                  </label>
                </div>

                <button
                  className="btn btn-danger btn-sm w-100"
                  onClick={handleDefinirIndisponibilite}
                >
                  <i className="bi bi-slash-circle me-1"></i>Définir l'indisponibilité
                </button>
              </div>
            )}

            {hasUnavailabilities && (
              <div className="mt-3">
                {unavailabilities.map((u, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center gap-2 mb-2 p-2 bg-danger bg-opacity-10 rounded-2"
                  >
                    <i className="bi bi-slash-circle text-danger"></i>
                    <div style={{ fontSize: 12 }}>
                      <span className="fw-semibold">{u.start_date}</span>
                      <span className="text-muted mx-1">→</span>
                      <span className="fw-semibold">{u.end_date}</span>
                      <div className="text-muted">
                        {u.start_time} — {u.end_time}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section Voyages ── */}
          <div className="p-3">
            <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
              Voyages prévus
            </p>
            <p className="text-muted small mt-1 mb-0">
              <i className="bi bi-calendar-x me-1"></i>Aucun voyage prévu ce jour.
            </p>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

/* ══════════════ Calendar ══════════════ */
function Calendar({ vehiculeId, mois }) {
  const [days, setDays] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(parseInt(mois));
  const axios = useAxios();

  const year = new Date().getFullYear();

  const getDayOfWeek = (date) => {
    const d = date.getDay();
    return d === 0 ? 6 : d - 1;
  };
  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

  const buildDays = (avails, unavails, y, m) => {
    const total = getDaysInMonth(y, m);
    const startDay = getDayOfWeek(new Date(y, m, 1));
    const result = Array.from({ length: startDay }, () => ({ spacer: true }));

    for (let d = 1; d <= total; d++) {
      const dayStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const avail = avails.find((a) => a.WeekDAY === String(getDayOfWeek(new Date(y, m, d))));
      const unavail = unavails.find(
        (u) => u.WeekDAY === String(getDayOfWeek(new Date(y, m, d))) && u.type === '0'
      );
      const unavailCustom = unavails.find(
        (u) => dayStr >= u.start_date && dayStr <= u.end_date && u.type === '1'
      );
      result.push({
        date: d,
        price: avail ? `${avail.Default_price}€` : null,
        unavailable: !avail || !!unavail || !!unavailCustom,
        WeekDAY: avail?.WeekDAY,
        VehiculeId: avail?.vehiculeId || vehiculeId,
      });
    }
    return result;
  };

  function fetchData() {
    axios
      .get('/availabilities/getbyvehiculeid/' + vehiculeId)
      .then(({ data }) => setAvailabilities(data.filter((i) => i.type == '0')))
      .catch(() => {});
    axios
      .get('/unavailabilities/getbyvehiculeid/' + vehiculeId)
      .then(({ data }) => setUnavailabilities(data))
      .catch(() => {});
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setDays(buildDays(availabilities, unavailabilities, year, currentMonth));
  }, [availabilities, unavailabilities, currentMonth]);

  const prevMonth = () => setCurrentMonth((m) => (m - 1 + 12) % 12);
  const nextMonth = () => setCurrentMonth((m) => (m + 1) % 12);

  const availCount = days.filter((d) => !d.spacer && !d.unavailable).length;
  const unavailCount = days.filter((d) => !d.spacer && d.unavailable).length;

  return (
    <>
      <ToastContainer />
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        {/* Header mois */}
        <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-3 px-3">
          <button
            className="btn btn-sm btn-light rounded-circle"
            onClick={prevMonth}
            style={{ width: 36, height: 36 }}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <div className="text-center">
            <p className="mb-0 fw-bold" style={{ fontSize: 16 }}>
              {MONTHS[currentMonth]} {year}
            </p>
            <div className="d-flex gap-3 justify-content-center mt-1">
              <small className="text-success">
                <i className="bi bi-circle-fill me-1" style={{ fontSize: 8 }}></i>
                {availCount} dispo
              </small>
              <small className="text-muted">
                <i className="bi bi-circle-fill me-1" style={{ fontSize: 8 }}></i>
                {unavailCount} indispo
              </small>
            </div>
          </div>
          <button
            className="btn btn-sm btn-light rounded-circle"
            onClick={nextMonth}
            style={{ width: 36, height: 36 }}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        {/* Corps calendrier */}
        <div className="card-body p-2 p-md-3">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7,1fr)',
              gap: '4px',
              marginBottom: '4px',
            }}
          >
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="text-center text-muted fw-semibold"
                style={{ fontSize: 11, padding: '4px 0' }}
              >
                {d}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
            {days.map((day, i) => (
              <React.Fragment key={i}>
                {day.spacer ? (
                  <div />
                ) : (
                  <CalendarDayButton
                    date={day.date}
                    price={day.price}
                    currentMonth={currentMonth}
                    WeekDAY={day.WeekDAY}
                    unavailable={day.unavailable}
                    VehiculeId={day.VehiculeId || vehiculeId}
                    onUnavailabilityAdded={fetchData}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Légende */}
        <div className="card-footer bg-body-tertiary border-top d-flex gap-3 flex-wrap px-3 py-2">
          <div className="d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: '#e8f4fd' }}></div>
            <span className="text-muted">Disponible</span>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: '#e9ecef' }}></div>
            <span className="text-muted">Indisponible</span>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                border: '2px solid #ff6600',
                background: '#e8f4fd',
              }}
            ></div>
            <span className="text-muted">Aujourd'hui</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default Calendar;
